import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Student FAQ — Tiger's Eye" },
      {
        name: "description",
        content:
          "Answers for students: is AI feedback allowed, what Tiger's Eye checks, how much it costs, and why it never writes your work for you.",
      },
      { property: "og:url", content: "https://tigerseye.app/faq" },
      { property: "og:title", content: "Student FAQ — Tiger's Eye" },
      {
        property: "og:description",
        content:
          "Answers for students: is AI feedback allowed, what Tiger's Eye checks, how much it costs, and why it never writes your work for you.",
      },
    ],
    links: [{ rel: "canonical", href: "https://tigerseye.app/faq" }],
  }),
  component: FaqPage,
});

const BG = "var(--paper)";
const SURFACE = "var(--card)";
const INK = "var(--primary)";
const INK_SOFT = "var(--ink-secondary)";
const INK_FAINT = "var(--ink-tertiary)";
const BRASS = "var(--editorial-accent, var(--accent))";
const RULE = "var(--rule)";

const SERIF =
  '"Iowan Old Style", "Source Serif Pro", "Source Serif 4", "Charter", Georgia, ui-serif, serif';

const FAQS: { q: string; a: string }[] = [
  {
    q: "Is it allowed to get AI feedback on my draft?",
    a: "Asking for feedback on your own writing is very different from asking AI to write it. Tiger's Eye only ever critiques your draft — it never writes, rewrites or generates content for you. Many universities explicitly welcome this kind of support, but rules vary, so always check your university's own policy and your module handbook.",
  },
  {
    q: "What does a review actually check?",
    a: "Your draft is checked against your assignment brief, your level of study and your referencing style. You get clear, section-by-section comments on argument, structure, evidence, clarity and referencing — the things markers look for.",
  },
  {
    q: "Will Tiger's Eye write or rewrite my essay?",
    a: "No, never. Tiger's Eye is a critique tool, not a writing tool. It points out what's working and what needs attention, and you decide what to change. Your work stays yours, and so does your grade.",
  },
  {
    q: "How much does it cost?",
    a: "Tiger's Eye is free for the first 49 students who sign up. After that it's £4.95 per term — less than a coffee a month. No hidden fees and no word-count charges.",
  },
  {
    q: "What can I upload?",
    a: "Essays, dissertations and reports as PDF, Word, or plain text, up to a sensible size limit. You can also paste your draft directly.",
  },
  {
    q: "Who can see my drafts?",
    a: "Only you. Your drafts and reviews are private to your account, never shared with other students, and never sold or used to advertise to you.",
  },
  {
    q: "Can I add my tutor's comments and grade afterwards?",
    a: "Yes — once your marked work comes back, you can add your tutor's feedback and grade to the review. It helps you compare what we flagged with what your marker said, and it makes future reviews better.",
  },
  {
    q: "Which referencing styles are supported?",
    a: "Harvard, APA, OSCOLA, MHRA and more — choose yours when you submit, and the review checks your citations and reference list against it.",
  },
];

export default function FaqPage() {
  const { user } = useAuth();
  const ctaTo = user ? "/reviews/new" : "/signup";

  return (
    <main className="min-h-screen w-full" style={{ background: BG, color: INK }}>
      <style>{`
        .hv4-serif { font-family: ${SERIF}; font-feature-settings: "liga","kern"; }
        .hv4-eyebrow {
          font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
          font-weight: 600; color: ${BRASS};
        }
        .hv4-cta { transition: transform .18s ease, box-shadow .18s ease, background .18s ease; }
        .hv4-cta-primary { background: ${INK}; color: ${BG}; }
        .hv4-cta-primary:hover { transform: translateY(-1px); box-shadow: 0 10px 22px -14px ${INK}; background: color-mix(in oklch, ${INK} 85%, black); }
      `}</style>

      <header style={{ borderBottom: `1px solid ${RULE}` }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="hv4-serif text-lg font-semibold" style={{ color: INK }}>
            Tiger&apos;s Eye
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/pricing" style={{ color: INK_SOFT }}>
              Pricing
            </Link>
            {user ? (
              <Link to="/dashboard" style={{ color: INK_SOFT }}>
                Dashboard
              </Link>
            ) : (
              <Link to="/login" style={{ color: INK_SOFT }}>
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-6 pb-24 pt-16">
        <Reveal>
          <p className="hv4-eyebrow">Questions</p>
          <h1 className="hv4-serif mt-4 text-balance text-4xl leading-tight sm:text-5xl">
            Student FAQ
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed" style={{ color: INK_SOFT }}>
            Everything students ask us before their first review. Something missing?{" "}
            <Link to="/versus" className="underline" style={{ color: INK }}>
              See how Tiger&apos;s Eye differs from AI writing tools
            </Link>
            .
          </p>
        </Reveal>

        <div className="mt-12 flex flex-col gap-4">
          {FAQS.map((item) => (
            <Reveal key={item.q}>
              <details
                className="group rounded-2xl p-6"
                style={{ background: SURFACE, border: `1px solid ${RULE}` }}
              >
                <summary className="cursor-pointer list-none text-base font-medium marker:hidden">
                  <span className="hv4-serif">{item.q}</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: INK_SOFT }}>
                  {item.a}
                </p>
              </details>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-12 text-center">
            <Link
              to={ctaTo}
              className="hv4-cta hv4-cta-primary inline-flex items-center gap-2 rounded-full px-7 py-4 text-base font-medium"
            >
              Start a free review
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      <footer
        className="mx-auto flex max-w-6xl items-center justify-between px-6 pb-10 text-xs"
        style={{ color: INK_FAINT }}
      >
        <span>© {new Date().getFullYear()} Tiger&apos;s Eye · by PRAXIA</span>
        <span className="flex items-center gap-4">
          <Link to="/pricing" style={{ color: INK_SOFT }}>
            Pricing
          </Link>
          <Link to="/terms" style={{ color: INK_SOFT }}>
            Terms &amp; ethos
          </Link>
        </span>
      </footer>
    </main>
  );
}
